// src/oura/delivery/Square.tsx
//
// The squares that mark every item, in the Checklist and the drawer: they are
// the bullet and the progress at once. Empty grey is to do, a violet outline
// is waiting (on Oura's re-check or on the client), solid green is done, and
// a red outline is a fix that came back.

import { CheckIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { TYPE } from "../ui/tokens";
import type { SquareState } from "./format";

const SQUARE: Record<SquareState, string> = {
    todo: "border-2 border-gray-300 bg-white",
    waiting: "border-2 border-violet-500 bg-white",
    done: "border-2 border-emerald-500 bg-emerald-500 text-white",
    came_back: "border-2 border-rose-600 bg-white",
};

const SQUARE_WORD: Record<SquareState, string> = {
    todo: "To do",
    waiting: "Waiting",
    done: "Done",
    came_back: "Came back",
};

export function Square({ state, className }: { state: SquareState; className?: string }) {
    return (
        <span aria-hidden className={cn("inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px]", SQUARE[state], className)}>
            {state === "done" && <CheckIcon size={10} strokeWidth={3} />}
        </span>
    );
}

const KEY: Array<{ state: SquareState; text: string }> = [
    { state: "todo", text: "nobody has done it yet" },
    { state: "waiting", text: "on Oura's re-check or on the client" },
    { state: "done", text: "confirmed by Oura, live, or ticked with proof" },
    { state: "came_back", text: "fixed before, found again by the latest scan" },
];

/** Where the tracker's tasks decide Done (#4682), Done is the sheet's word. */
const TASKS_DONE_TEXT = "every task Done in the tracker sheet, or confirmed by Oura where no task carries the code";

/** What the four squares mean, in plain words, above the checklist. */
export function SquareKey({ className, tasksDecide = false }: { className?: string; tasksDecide?: boolean }) {
    return (
        <ul className={cn("m-0 flex list-plain flex-wrap gap-x-5 gap-y-1.5 p-0", className)} aria-label="What the squares mean">
            {KEY.map((k) => (tasksDecide && k.state === "done" ? { ...k, text: TASKS_DONE_TEXT } : k)).map((k) => (
                <li key={k.state} className={cn(TYPE.tc, "flex items-center gap-1.5 text-gray-600")}>
                    <Square state={k.state} />
                    <span>
                        <span className="font-medium text-gray-900">{SQUARE_WORD[k.state]}</span> {k.text}
                    </span>
                </li>
            ))}
        </ul>
    );
}
